import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomersService } from 'src/app/services/core/quickbook/customers/customers.service';
import { InvoicePaymentService } from 'src/app/services/core/quickbook/invoice-payments/invoice-payment.service';
import {
  NUMBER_OF_RECORDS,
  PAYMENT_ENTITY_STAT_CARDS,
  PAYMENT_ENTITY_AMOUNT_STAT_CARDS,
} from 'src/app/utils/common.utils';
import { sweetAlert } from 'src/app/utils/swal';
import { debounceTime, filter, Subject } from 'rxjs';
import * as moment from 'moment';

@UntilDestroy()

@Component({
  selector: 'app-invoice-payments-list',
  templateUrl: './invoice-payments-list.component.html',
  styleUrls: ['./invoice-payments-list.component.css']
})
export class InvoicePaymentsListComponent implements OnInit {

  payments: any = [];
  totalPages = 0;
  totalResults = 0;
  search: any = '';
  private searchSubject = new Subject<string>();
  currentPage = 1;
  selectedCustomerId = null;
  selectedDepositStatus = '';
  dateRange: any = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
  stats: any = {};
  statusStatCards = PAYMENT_ENTITY_STAT_CARDS;
  amountStatCards = PAYMENT_ENTITY_AMOUNT_STAT_CARDS;
  customers: any;
  selectedDropdownIndex: any;
  toggleActionButtons = false;
  numberOfRecords = NUMBER_OF_RECORDS;

  constructor(
    private customersService: CustomersService,
    private invoicePaymentService: InvoicePaymentService,
  ) { }

  ngOnInit(): void {
    this.getCustomers();
    this.initializeSearch();
    this.getPayments(this.currentPage);
  }

  getPayments(page: any) {
    const queryParams: any = { limit: this.numberOfRecords, page };
    if (this.search) queryParams.search = this.search.trim();
    if (this.selectedCustomerId) queryParams.customer = this.selectedCustomerId;
    if (this.selectedDepositStatus) queryParams.depositStatus = this.selectedDepositStatus;
    const dates = this.dateQuery();
    if (dates.startDate) {
      queryParams.startDate = dates.startDate;
      queryParams.endDate = dates.endDate;
    }
    this.invoicePaymentService.getInvoicePayments(queryParams).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          const { data: rows = [], overview = {} } = (data?.[0] || {}) as any;
          this.payments = rows;
          this.totalPages = overview.totalPages;
          this.totalResults = overview.totalResults;
          this.stats = overview;
        }
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getPayments(page);
  }

  onCustomerFilterChange() {
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  onDepositStatusFilterChange() {
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  onDateChange(event: any) {
    if (!event?.[0] && !event?.[1]) {
      this.dateRange = null;
      this.currentPage = 1;
      this.getPayments(this.currentPage);
      return;
    }
    if (!event?.[0] || !event?.[1]) return;
    this.dateRange = event;
    this.currentPage = 1;
    this.getPayments(this.currentPage);
  }

  dateQuery() {
    if (!this.dateRange?.[0] || !this.dateRange?.[1]) return {};
    return {
      startDate: moment(this.dateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(this.dateRange[1]).format('YYYY-MM-DD'),
    };
  }

  getCustomers() {
    this.customersService.getCustomers({ limit: 9999 }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) this.customers = (data.results || []).filter((c: any) => c.Id);
    });
  }

  initializeSearch() {
    this.searchSubject.pipe(
      debounceTime(500),
      filter((s: any) => !s || s.length >= 3),
      untilDestroyed(this)
    ).subscribe(() => {
      this.currentPage = 1;
      this.getPayments(this.currentPage);
    });
  }

  onSearchInput() {
    this.searchSubject.next(this.search);
  }

  async onSync(payment: any, type: 'pull' | 'push') {
    const result = await sweetAlert(
      'Alert',
      type === 'pull'
        ? 'Are you sure you want to overwrite payment data with QuickBooks data?'
        : 'Are you sure you want to overwrite the QuickBooks data with payment data?',
      'warning', 'Yes', 'No'
    );
    if (!result.value) return;
    if (type === 'pull') payment.pulling = true; else payment.pushing = true;
    this.closeActionMenu(null);
    this.invoicePaymentService.syncInvoicePayment(payment.id, { isSyncFrom: type === 'pull' })
      .pipe(untilDestroyed(this)).subscribe({
        next: (data: any) => {
          this.getPayments(this.currentPage);
          sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
        },
        error: (err) => {
          sweetAlert('Error', err, 'error', 'Ok');
          payment.pulling = false;
          payment.pushing = false;
        },
        complete: () => { payment.pulling = false; payment.pushing = false; },
      });
  }

  async onVoid(payment: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to void this payment? The invoice balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.toggleActionButtons = false;
    this.invoicePaymentService.voidPayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.getPayments(this.currentPage);
        sweetAlert('Success', 'Payment voided', 'success', 'Ok');
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  async onDelete(payment: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to delete this payment? The invoice balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.toggleActionButtons = false;
    this.invoicePaymentService.deletePayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.getPayments(this.currentPage);
        sweetAlert('Success', 'Payment deleted', 'success', 'Ok');
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok'),
    });
  }

  showActionButtons(idx: any) {
    this.selectedDropdownIndex = idx;
    this.toggleActionButtons = true;
  }

  closeActionMenu(_evt: any) {
    this.selectedDropdownIndex = -1;
    this.toggleActionButtons = false;
  }
}
